/**
 * Token Service
 *
 * With httpOnly cookies, the access and refresh tokens are managed by
 * the browser and are NOT accessible from JavaScript.
 *
 * This service now manages:
 * - Token expiry tracking (expiresAt in localStorage, for scheduling proactive refresh)
 * - Device ID (in localStorage, not a secret)
 * - Auth state detection (via the readable CSRF cookie as a proxy)
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const TOKEN_EXPIRY_KEY = '121pass_token_expiry';
const DEVICE_ID_KEY = '121pass_device_id';

// ============================================================================
// CSRF TOKEN (sessionStorage + in-memory)
// ============================================================================

const CSRF_STORAGE_KEY = '121pass_csrf';

/**
 * Get CSRF token — sessionStorage first, cookie fallback (same-origin/localhost).
 * sessionStorage survives page refreshes but clears when the browser tab closes.
 */
function getCsrfToken(): string | null {
  try {
    const stored = sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Ignore storage errors
  }
  const match = document.cookie.match(/(?:^|;\s*)p121_csrf_admin=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function setCsrfToken(token: string | null): void {
  try {
    if (token) {
      sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(CSRF_STORAGE_KEY);
      // Also expire the browser cookie — it's not httpOnly so JS can clear it.
      // This matters when the server-side logout fails (e.g. 401 on the DELETE
      // session endpoint): the backend can't clear its own cookie, but the CSRF
      // cookie (readable by JS) would survive and make hasToken() return true
      // on the next page load, causing an immediate RefreshAuthError loop.
      document.cookie =
        'p121_csrf_admin=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    }
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// TOKEN SERVICE
// ============================================================================

export const tokenService = {
  // --- Token expiry (localStorage) ---

  setTokenExpiry(expiresAt: number): void {
    try {
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
    } catch {
      // Ignore storage errors
    }
  },

  getTokenExpiry(): number | null {
    try {
      const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
      return expiry ? parseInt(expiry, 10) : null;
    } catch {
      return null;
    }
  },

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;
    const now = Math.floor(Date.now() / 1000);
    return now >= expiry;
  },

  // --- Device ID (localStorage) ---

  generateDeviceId(): string {
    const uuid =
      crypto.randomUUID?.() ||
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    return `web-admin-${uuid}`;
  },

  setDeviceId(deviceId: string): void {
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } catch {
      // Ignore storage errors
    }
  },

  getDeviceId(): string {
    try {
      let deviceId = localStorage.getItem(DEVICE_ID_KEY);
      if (!deviceId) {
        deviceId = this.generateDeviceId();
        this.setDeviceId(deviceId);
      }
      return deviceId;
    } catch {
      return this.generateDeviceId();
    }
  },

  getExistingDeviceId(): string | null {
    try {
      return localStorage.getItem(DEVICE_ID_KEY);
    } catch {
      return null;
    }
  },

  // --- Auth state detection ---

  /**
   * Check if auth cookies exist.
   * Uses the readable CSRF cookie as a proxy for httpOnly auth cookies.
   */
  hasToken(): boolean {
    return !!getCsrfToken();
  },

  isAuthenticated(): boolean {
    return this.hasToken() && !this.isTokenExpired();
  },

  // --- CSRF ---

  getCsrfToken,
  setCsrfToken,

  // --- Cleanup ---

  /**
   * Clear local data. httpOnly cookies are cleared server-side via /auth/logout.
   */
  /**
   * Limpia el estado de sesión.
   *
   * OJO: el `deviceId` NO se borra a propósito. No es un secreto — es la
   * identidad estable de ESTE navegador, y es la clave con la que el backend
   * agrupa la sesión (`sessions` tiene único `(userId, deviceId)`). Si se
   * borrara en cada logout, el siguiente login crearía una fila nueva y la
   * pantalla de "dispositivos vinculados" se llenaría de duplicados del mismo
   * equipo, volviendo inútil el cierre remoto de sesión.
   *
   * Para olvidar el dispositivo por completo existe `forgetDevice()`.
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch {
      // Ignore storage errors
    }
    setCsrfToken(null);
  },

  /**
   * Descarta también la identidad del navegador. Sólo para casos en los que
   * se quiere empezar de cero de verdad (p. ej. una herramienta de soporte).
   */
  forgetDevice(): void {
    try {
      localStorage.removeItem(DEVICE_ID_KEY);
    } catch {
      // Ignore storage errors
    }
  },
};
